import type { InconsistenciesQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { FilterVariables } from 'modules/core/components/MultiFilters/hooks/types'
import { CustomEvaluatorFn } from 'modules/core/components/MultiFilters/hooks/utils/applyFilters'
import type { Value } from 'modules/core/components/MultiFilters/hooks/utils/defaultEvaluators'
import type { Employee as CurrentEmployee } from 'modules/core/hooks/useCurrentSession'

import { AlertType } from 'modules/attendance/lib/types'
import { employeeClockedIn, employeePresence } from 'modules/attendance/store/collections/presences'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Leave from 'modules/timeoff/store/models/Leave'

import employeeStatuses from './employeeStatuses'

type TimeInsightsInconsistency =
  InconsistenciesQuery['timeInsights']['inconsistenciesConnection']['nodes'][number]

const leaveEmployee = (leave: Leave) => {
  return employees.relatedTo(leave)
}

const isEmployeeTimeoffManagedByCurrent = (
  employee: Employee,
  currentEmployee: CurrentEmployee | undefined
) => {
  if (!currentEmployee) return false

  return currentEmployee.id === employee.attributes.timeoff_manager_id
}

const todayAbsentees = () => {
  const today = moment().startOf('day')

  return leaves.models
    .filter((leave) => {
      const start = leave.attributes.start_on
      const finish = leave.attributes.finish_on

      if (!finish) {
        return today.isSameOrAfter(start)
      }

      return today.isSameOrAfter(start) && today.isSameOrBefore(finish)
    })
    .map((leave) => leaveEmployee(leave))
}

const thisMonthAbsentees = () => {
  const startOfMonth = moment().startOf('month')

  return leaves.models
    .filter((leave) => {
      const start = leave.attributes.start_on
      const finish = leave.attributes.finish_on

      if (!finish) {
        return startOfMonth.isSameOrBefore(start)
      }

      return startOfMonth.isSameOrBefore(start) || startOfMonth.isSameOrBefore(finish)
    })
    .map((leave) => leaveEmployee(leave))
}

const getInconsistencyStatus = ({
  employeeId,
  inconsistencyType,
  shiftInconsistencies,
}: {
  employeeId: number
  inconsistencyType: string
  shiftInconsistencies: TimeInsightsInconsistency[] | undefined
}) => {
  if (!shiftInconsistencies) return false

  const hasInconsistencyType = shiftInconsistencies.find(
    (inconsistency) =>
      inconsistency?.employeeId === employeeId &&
      inconsistency?.inconsistencyType === inconsistencyType
  )

  return !!hasInconsistencyType
}

const employeeEvaluator: CustomEvaluatorFn = (
  employee,
  filterValue,
  currentEmployee,
  allMyDescendants,
  variables = {}
) => {
  if (!employee) return false

  const { shiftInconsistencies } = variables

  switch (filterValue) {
    case employeeStatuses.active:
      return !employee.isTerminating

    case employeeStatuses.terminated:
      return !!employee.isTerminating

    case employeeStatuses.timeoffManagedByMe:
      return isEmployeeTimeoffManagedByCurrent(employee, currentEmployee)

    case employeeStatuses.locationIssues:
      const { aggregateShiftLocationsIncidents } = variables
      if (!aggregateShiftLocationsIncidents) return false

      const shiftLocaltionIncident = aggregateShiftLocationsIncidents.find(
        (shiftLocation) => shiftLocation.employeeId === employee.id
      )

      if (!shiftLocaltionIncident) return false

      return shiftLocaltionIncident.shiftLocationsCount > 0

    case employeeStatuses.reportingToMe:
      return allMyDescendants.some((descendantId) => descendantId === employee.id)

    case employeeStatuses.reportingToMeDirectly:
      return employee.attributes.manager_id === currentEmployee?.id

    case employeeStatuses.away:
      return (todayAbsentees().map((e) => e.attributes.id) || []).includes(employee.attributes.id)

    case employeeStatuses.awayThisMonth:
      return (thisMonthAbsentees().map((e) => e.attributes.id) || []).includes(
        employee.attributes.id
      )

    case employeeStatuses.clockIn:
      return !!employeeClockedIn(employee)

    case employeeStatuses.onBreak:
      return !!employeePresence(employee).attributes.on_break

    case employeeStatuses.missingClockIn:
      const { clockInStatuses } = variables

      if (!clockInStatuses) return false

      const hasMissingClockIn = clockInStatuses.find(
        (clockInStatus) =>
          clockInStatus.employee?.id === employee.id && clockInStatus.status == 'missing_clock_in'
      )

      return !!hasMissingClockIn

    case employeeStatuses.withWelcomeSpaceAccess:
      return !hasRegularAccess(employee.attributes.has_regular_access) && !employee.isTerminating

    case employeeStatuses.forgotToClockIn:
      return getInconsistencyStatus({
        employeeId: employee.id,
        inconsistencyType: AlertType.missingClockIn,
        shiftInconsistencies,
      })

    case employeeStatuses.missingClockOut:
      return getInconsistencyStatus({
        employeeId: employee.id,
        inconsistencyType: AlertType.missingClockOut,
        shiftInconsistencies,
      })

    case employeeStatuses.insufficientBreak:
      return getInconsistencyStatus({
        employeeId: employee.id,
        inconsistencyType: AlertType.insufficientBreakDuration,
        shiftInconsistencies,
      })

    case employeeStatuses.missingBreakTime:
      return getInconsistencyStatus({
        employeeId: employee.id,
        inconsistencyType: AlertType.missingBreakTime,
        shiftInconsistencies,
      })

    case employeeStatuses.alertsAllTypes:
      return (
        getInconsistencyStatus({
          employeeId: employee.id,
          inconsistencyType: AlertType.missingClockIn,
          shiftInconsistencies,
        }) ||
        getInconsistencyStatus({
          employeeId: employee.id,
          inconsistencyType: AlertType.missingClockOut,
          shiftInconsistencies,
        }) ||
        getInconsistencyStatus({
          employeeId: employee.id,
          inconsistencyType: AlertType.insufficientBreakDuration,
          shiftInconsistencies,
        }) ||
        getInconsistencyStatus({
          employeeId: employee.id,
          inconsistencyType: AlertType.missingBreakTime,
          shiftInconsistencies,
        })
      )

    default:
      return false
  }
}

const notEmployeeEvaluator = (
  employee: Employee,
  filterValue: Value,
  manager: CurrentEmployee,
  allMyDescendants: number[],
  variables: FilterVariables = {}
): boolean => !employeeEvaluator(employee, filterValue, manager, allMyDescendants, variables)

const employeeEvaluators = {
  employee_status: {
    is_equal: employeeEvaluator,
    is_not_equal: notEmployeeEvaluator,
  },
  clockin_status: {
    is_equal: employeeEvaluator,
    is_not_equal: notEmployeeEvaluator,
  },
  alerts: {
    is_equal: employeeEvaluator,
    is_not_equal: notEmployeeEvaluator,
  },
}

export default employeeEvaluators
